/**
 * PostgreSQL Language Service Provider
 *
 * Implements the LanguageServiceProvider interface for PostgreSQL
 * using Supabase's Postgres Language Server via LSP over stdio.
 *
 * Completion is fully orchestrator-driven (`pgsqlResolver` + schema cache) —
 * only the mock engine's LSP bridge ever consults a provider for completion
 * items, so `getCompletionItems` below is an explicit stub. (Workstream A —
 * legacy per-engine completion layer retirement; the old arm called the raw
 * pgls LSP completion endpoint and post-filtered it with a schema-snapshot
 * column allowlist.)
 */

import {
  closeDocument,
  connectToDatabase,
  disconnectDatabase,
  getHoverInfo,
  getSignatureHelp,
  onDiagnostics,
  onIntelliSenseStatus,
  getIntelliSenseStatus,
  registerDocument,
  updateDocument,
} from './pgLanguageServiceSync';
import type {
  LanguageServiceProvider,
  LanguageServiceCompletionResult,
  LanguageServiceConnectionInfo,
  LanguageServicePosition,
} from './languageServiceProvider';
import { getSnapshot as getSchemaBusSnapshot } from '@/features/intellisense/schemaBus/schemaBus';
import { ensurePgSchema, getCachedPgSchema } from './pgSchemaCache';

type DocumentConnectionState = {
  connectionId: string;
  database: string;
};

const documentConnections = new Map<string, DocumentConnectionState>();

export const pgLanguageServiceProvider: LanguageServiceProvider = {
  engine: 'pgsql',

  registerDocument: (uri: string, text: string) =>
    registerDocument(uri, text),

  updateDocument: (uri: string, text: string) =>
    updateDocument(uri, text),

  closeDocument: (uri: string) => {
    documentConnections.delete(uri);
    return closeDocument(uri);
  },

  connectForLanguageServices: async (
    uri: string,
    connectionInfo: LanguageServiceConnectionInfo
  ): Promise<boolean> => {
    const result = await connectToDatabase(
      uri,
      connectionInfo.connectionId,
      connectionInfo.database
    );

    if (result.success) {
      const connectionId = connectionInfo.connectionId;
      const database = connectionInfo.database?.trim() || '';
      documentConnections.set(uri, { connectionId, database });

      // S7 item 3 — the snapshot is built once per (connection, database) and
      // rebuilt only by DDL invalidation, an explicit refresh, or reconnect.
      // Connect therefore forces ONLY when there is nothing to serve: no
      // native snapshot at all, or a bus that was cleared (disconnect /
      // reconnect, `invalidateConnection`) while the native cache stayed warm
      // — the cache's warm path returns without re-publishing, so without this
      // the reconnected editor would sit on an empty bus. A warm snapshot plus
      // a warm bus re-uses both and issues no introspection at all.
      const existingSchema = getCachedPgSchema(connectionId);
      const hasBusSnapshot = Boolean(getSchemaBusSnapshot(connectionId, database));

      // Build schema snapshot in the background (don't block connection)
      ensurePgSchema({
        connectionId,
        database,
        forceRefresh: !existingSchema || !hasBusSnapshot,
      }).catch((error) => {
        console.warn('[PgLanguageServiceProvider] Failed to build schema snapshot:', error);
      });
    }

    return result.success;
  },

  disconnectForLanguageServices: (uri: string) => {
    documentConnections.delete(uri);
    return disconnectDatabase(uri);
  },

  onDiagnostics: (listener) => onDiagnostics(listener),
  onIntelliSenseStatus: (listener) => onIntelliSenseStatus(listener),
  getIntelliSenseStatus: (uri) => getIntelliSenseStatus(uri),

  // PG completion is fully orchestrator-driven (`pgsqlResolver` + schema
  // cache). Only the mock engine's LSP bridge ever consults a provider for
  // completion items, so this arm is intentionally empty — it must never
  // become a second completion source.
  getCompletionItems: async (): Promise<LanguageServiceCompletionResult> => ({ items: [] }),

  getHoverInfo: (uri: string, position: LanguageServicePosition) =>
    getHoverInfo(uri, position.line, position.character),

  getSignatureHelp: (uri: string, position: LanguageServicePosition) =>
    getSignatureHelp(uri, position.line, position.character),
};
